/**
 * Password-based encryption/decryption utilities for datasource export/import
 * Uses AES-GCM with PBKDF2 key derivation for secure file encryption
 */

export interface DatasourceExportData {
  displayName: string
  databaseType: 'PostgreSQL' | 'MySQL' | 'MSSQL' | 'Redis' | 'RedisCluster' | 'MongoDB' | 'Cassandra' | 'SQLite' | 'OracleDB' | 'Memcached' | 'ClickHouse' | 'ScyllaDB' | 'Milvus'
  host: string
  port: number
  username: string
  password?: string
  agentId?: string
  defaultDatabase?: string | null
  connectionParams?: Record<string, unknown> | null
  isLocked?: boolean
}

export interface DatasourceExportFile {
  version: number
  salt: string
  iv: string
  data: string
  createdAt: string
}

const EXPORT_VERSION = 1
const PBKDF2_ITERATIONS = 100000
const SALT_LENGTH = 16
const IV_LENGTH = 12

/**
 * Convert Uint8Array to base64 string
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
}

/**
 * Convert base64 string to Uint8Array
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Derive an AES-GCM key from a password using PBKDF2
 */
async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder()
  const passwordBuffer = encoder.encode(password)

  const baseKey = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  )

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256'
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

/**
 * Encrypt datasource data with a password
 * @param datasources Array of datasources to export
 * @param password User-provided password for encryption
 * @returns Encrypted export file content as JSON string
 */
export async function encryptDatasourcesForExport(
  datasources: DatasourceExportData[],
  password: string
): Promise<string> {
  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH))
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))

  // Derive key from password
  const key = await deriveKey(password, salt)

  // Encrypt the data
  const encoder = new TextEncoder()
  const plaintext = encoder.encode(JSON.stringify(datasources))

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    plaintext
  )

  // Create export file structure
  const exportFile: DatasourceExportFile = {
    version: EXPORT_VERSION,
    salt: uint8ArrayToBase64(salt),
    iv: uint8ArrayToBase64(iv),
    data: uint8ArrayToBase64(new Uint8Array(ciphertext)),
    createdAt: new Date().toISOString()
  }

  return JSON.stringify(exportFile, null, 2)
}

/**
 * Decrypt datasources from an exported file
 * @param fileContent Encrypted export file content
 * @param password User-provided password for decryption
 * @returns Array of decrypted datasources
 * @throws Error if decryption fails (wrong password or corrupted file)
 */
export async function decryptDatasourcesFromImport(
  fileContent: string,
  password: string
): Promise<DatasourceExportData[]> {
  // Parse the export file
  let parsed: unknown
  try {
    parsed = JSON.parse(fileContent)
  } catch (e) {
    throw new Error('Invalid export file: Unable to parse JSON', { cause: e })
  }
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('Invalid export file format')
  }
  const exportFile = parsed as DatasourceExportFile

  // Validate version
  if (exportFile.version !== EXPORT_VERSION) {
    throw new Error(`Unsupported export file version: ${exportFile.version}`)
  }

  // Validate required fields
  if (!exportFile.salt || !exportFile.iv || !exportFile.data) {
    throw new Error('Invalid export file: Missing required fields')
  }

  // Decode base64 values
  let salt: Uint8Array
  let iv: Uint8Array
  let ciphertext: Uint8Array
  try {
    salt = base64ToUint8Array(exportFile.salt)
    iv = base64ToUint8Array(exportFile.iv)
    ciphertext = base64ToUint8Array(exportFile.data)
  } catch (e) {
    throw new Error('Invalid export file: Corrupted data', { cause: e })
  }

  // Derive key from password
  const key = await deriveKey(password, salt)

  // Decrypt the data
  let plaintext: ArrayBuffer
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext
    )
  } catch (e) {
    throw new Error('Decryption failed: Incorrect password or corrupted file', { cause: e })
  }

  // Parse the decrypted data
  const decoder = new TextDecoder()
  const jsonString = decoder.decode(plaintext)

  let datasources: unknown
  try {
    datasources = JSON.parse(jsonString)
  } catch (e) {
    throw new Error('Decryption failed: Invalid data format', { cause: e })
  }

  // Validate the structure
  if (!Array.isArray(datasources)) {
    throw new Error('Invalid export file: Expected array of datasources')
  }

  // Validate each entry
  for (const entry of datasources) {
    if (typeof entry !== 'object' || entry === null) {
      throw new Error('Invalid export file: Invalid datasource entry')
    }
    const dsEntry = entry as Record<string, unknown>
    if (typeof dsEntry.displayName !== 'string' || typeof dsEntry.databaseType !== 'string') {
      throw new Error('Invalid export file: Missing required datasource fields')
    }
  }

  return datasources as DatasourceExportData[]
}

/**
 * Download the exported file to user's computer
 * @param content File content to download
 * @param filename Name of the file
 */
export function downloadExportFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  // Use detached link to avoid React DOM conflicts
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * Read file content from a File object
 * @param file File object from file input
 * @returns File content as string
 */
export async function readImportFile(file: File): Promise<string> {
  return file.text()
}
